import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, IconButton, Typography, Button, CircularProgress,
  Box, Checkbox, FormControlLabel, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneIcon from "@mui/icons-material/Tune";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// ⚠️  These strings MUST exactly match the `label` in Dashboard's quickAccessConfig
const ALL_OPTIONS = [
  "Earnings Calendar",
  "Top Yielding Stocks",
  "Latest FA Ratings",
];

const MyEditDialog = ({ open, onClose, sections, setSections, saving, setSaving }) => {
  const [showAllChecked, setShowAllChecked] = useState(true);

  const handleDragEnd = (result) => {
    if (!result.destination || saving) return;
    const updated = Array.from(sections);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setSections(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      onClose();
    } catch (err) {
      console.error("Failed to save MyFinlytic config", err);
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!saving ? onClose : undefined}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)",
          backgroundImage: "none",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--color-border-primary)",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, px: 2.5 }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "var(--color-surface-hover)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TuneIcon sx={{ fontSize: 16, color: "var(--color-primary)" }} />
          </Box>
          <Box>
            <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">My FactorAlpha</Typography>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight="var(--font-weight-medium)">Customise your dashboard layout</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"
          sx={{ color: "var(--color-text-secondary)", "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-primary)" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.5, px: 2.5, bgcolor: "var(--color-bg-secondary)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-muted)">
            Drag to reorder · toggle to show/hide
          </Typography>
          <FormControlLabel
            label={<Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">Show all</Typography>}
            control={
              <Checkbox size="small" disabled={saving} checked={showAllChecked}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowAllChecked(checked);
                  setSections((prev) => prev.map((sec) => ({ ...sec, visible: checked })));
                }}
                sx={{ color: "var(--color-border-secondary)", "&.Mui-checked": { color: "var(--color-primary)" } }}
              />
            }
          />
        </Stack>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="finlytic-sections">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {sections
                  .filter((s) => ALL_OPTIONS.includes(s.label))
                  .map((section, index) => (
                    <Draggable key={section.id} draggableId={section.id} index={index} isDragDisabled={saving}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}
                          style={{ ...provided.draggableProps.style, display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: snapshot.isDragging ? "var(--color-surface-active)" : "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-md)", padding: "10px 12px", marginBottom: 8, transition: "background 0.15s" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-muted)" sx={{ width: 16, textAlign: "center" }}>{index + 1}</Typography>
                            <span {...provided.dragHandleProps} style={{ cursor: "grab", display: "flex", color: "var(--color-text-muted)" }}>
                              <DragIndicatorIcon sx={{ fontSize: 18 }} />
                            </span>
                            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)">{section.label}</Typography>
                          </Box>
                          <Switch size="small"
                            checked={section.visible}   // ON = visible
                            disabled={saving}
                            onChange={() => setSections((prev) => prev.map((sec) => sec.id === section.id ? { ...sec, visible: !sec.visible } : sec))}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-primary)" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-primary)" } }}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)", bgcolor: "var(--color-surface)" }}>
        <Button onClick={onClose} disabled={saving}
          sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--color-text-secondary)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={11} color="inherit" /> : null}
          sx={{ textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)", bgcolor: "var(--color-primary)", boxShadow: "none", "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" } }}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyEditDialog;
